import React from 'react'
import { Typography } from '@mui/material'
import { HelpDrawerWithAccordion } from '@labrav/common'
import { makeStyles } from 'tss-react/mui'
import {
  FaqQuestion,
  HelpDrawerFaqItems,
} from '../../../components/AddEditProductFormComponents/FlyoutProductFormHelpFaqs/ProductCreationFaqUtil'

const FAQ_SECTION = 'awsRenewalPricingTerms'

const useStyles = makeStyles()(theme => ({
  introTitle: {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.h6.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: theme.typography.h6.lineHeight,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
  },
  introBody: {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.body2.fontSize,
    lineHeight: theme.typography.body2.lineHeight,
    color: theme.palette.text.secondary,
  },
}))

const faqItems: HelpDrawerFaqItems[] = [
  {
    question: (
      <FaqQuestion
        section={FAQ_SECTION}
        question="What happens if I select No Renewal Terms?"
      />
    ),
    answer: (
      <div>
        The contract will expire on the contract end date. No automatic renewal
        will occur.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FAQ_SECTION}
        question="What is Renewal with No Price Uplift?"
      />
    ),
    answer: (
      <div>
        The contract will automatically renew at the same prices as the current
        contract. No price increase will be applied to contract, usage, or
        installment prices.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FAQ_SECTION}
        question="What is Renewal with Price Uplift?"
      />
    ),
    answer: (
      <div>
        <div>
          The contract will automatically renew, and the renewal prices will be
          increased based on the uplift configuration you specify.
        </div>
        <div>You can choose one of the following pricing strategies:</div>
        <ul>
          <li>Fixed Percentage</li>
          <li>Percentage Range</li>
        </ul>
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion section={FAQ_SECTION} question="What is Definite Renewal?" />
    ),
    answer: (
      <div>
        Definite Renewal allows you to specify the maximum number of times the
        contract can automatically renew. After the configured number of
        renewals is reached, the contract expires.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FAQ_SECTION}
        question="What is Unlimited Renewal?"
      />
    ),
    answer: (
      <div>
        Unlimited Renewal allows the contract to continue renewing automatically
        until either party opts out.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FAQ_SECTION}
        question="What is the opt-out Period?"
      />
    ),
    answer: (
      <div>
        <div>
          The opt-out Period defines how many days before the contract end date
          the renewal becomes final.
        </div>
        <div>Once the opt-out period begins:</div>
        <ul>
          <li>Buyer cannot opt out of the renewal.</li>
          <li>Seller cannot opt out of the renewal.</li>
        </ul>
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FAQ_SECTION}
        question="What is the Adjustment Deadline?"
      />
    ),
    answer: (
      <div>
        <div>
          The Adjustment Deadline specifies how many days before the contract
          end date the seller must finalize the renewal pricing when using
          Percentage Range.
        </div>
        <div>After the Adjustment Deadline:</div>
        <ul>
          <li>Seller cannot modify the final uplift percentage.</li>
          <li>
            If no percentage is selected, AWS automatically applies the Default
            Percentage.
          </li>
        </ul>
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FAQ_SECTION}
        question="What is Default Percentage?"
      />
    ),
    answer: (
      <div>
        Default Percentage is the percentage automatically used by AWS if the
        seller does not finalize renewal pricing before the Adjustment Deadline.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FAQ_SECTION}
        question="What are Renewal Installments?"
      />
    ),
    answer: (
      <div>
        <div>
          For installment-based Private Offers, the renewal installment schedule
          is automatically generated using the original payment schedule.
        </div>
        <div>
          The installment schedule, payment percentages, and charge timing are
          pre-populated based on the original contract. Users can edit
          installment rows.
        </div>
        <ul>
          <li>
            Months after New Contract — This defines when each installment will
            be charged after the renewal contract begins.
          </li>
          <li>
            Day of the Month — This specifies the calendar day on which an
            installment should be charged. If left blank, AWS charges the
            installment on the natural date calculated using the renewal start
            date and the configured month offset.
          </li>
        </ul>
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FAQ_SECTION}
        question="Does Renewal affect Usage Dimensions?"
      />
    ),
    answer: (
      <div>
        Yes. If the Private Offer contains usage dimensions and a price uplift
        is configured, the renewal uplift is also applied to supported usage
        dimension prices according to the configured renewal pricing strategy.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FAQ_SECTION}
        question="Can either party opt out after the opt-out Period begins or Opt-out date passed?"
      />
    ),
    answer: (
      <div>
        No. Once the opt-out Period starts, neither the buyer nor the seller can
        opt out of the renewal.
      </div>
    ),
  },
]

const RenewalTermsIntro = () => {
  const { classes } = useStyles()
  return (
    <div>
      <Typography className={classes.introTitle} component="h3">
        What are Renewal Terms?
      </Typography>
      <Typography className={classes.introBody} component="p">
        Renewal Terms allow you to configure how your Private Offer will renew
        automatically when the current contract expires. Instead of creating a
        new Private Offer for every renewal, you can predefine the renewal
        behavior while creating the initial offer.
      </Typography>
    </div>
  )
}

export const PrivateOfferRenewalPricingTermsFaqs: React.FC = () => (
  <HelpDrawerWithAccordion
    headerTitle="Help & FAQs"
    faqTitle="Frequently Asked Questions"
    faqItems={faqItems}
    customContent={<RenewalTermsIntro />}
    divider
  />
)
